import React, { useCallback, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";

import "../../../components/image-upload/ImageUpload.scss";
import DisplayImage from "../../../components/data-components/DisplayImage";
import { HeaderProfile } from "../../../components/svg/svgImageList";
import { Divider, Grid } from "@mui/material";
import { CreatedByField, DateCreatedField, EmailField, FirstNameField, LastNameField, PhoneField, UserNameField } from "../../../components/form-fields/UserFields";
import Form from "../../../components/form/Form";
import { UploadImageStateModel } from "../../../components/image-upload/ImageUploadModel";
import { FormikHelpers, FormikValues } from "formik";
import { UserProfileEditInput } from "../../../api-models/request/UserInput";
import { ChangePassword } from "../../../components/data-components/ChangePassword";
import { warningLeavePageService } from "../../../services/page/WarningLeavePageService";
import useErrorHandler, { ApplicationError } from "../../../services/error-handler/ErrorHandlerService";
import { translationService } from "../../../services/translation/TranslationService";
import AddressFieldsForm from "../../../components/form-fields/AddressFieldsForm";
import ImageUpload from "../../../components/image-upload/ImageUpload";
import { currentUserService } from "../../../services/user/CurrentUserStorage";

export default observer((props: {currentUserProfileStorage: any, withAddress?: boolean, edit:boolean, onEditCancel: () => void}) => {
  const {currentUserProfileStorage, edit, onEditCancel, withAddress} = {...props};
  const { handleErrorInSnackbar } = useErrorHandler();

  const [imageUploadState] = useState(() => UploadImageStateModel.create({}));

  const getInitialValues = useCallback(
    () => ({...currentUserProfileStorage.currentUser}),
    [currentUserProfileStorage.currentUser]
  );

  const updateUserImage = useCallback(async () => {
    if (imageUploadState.shouldDeleteCurrentImage) {
      imageUploadState.removeImage();
      return currentUserProfileStorage.deleteMyUserProfileImage();
    }
    if (imageUploadState.imageForUpload)
      return currentUserProfileStorage.editMyUserProfileImage(imageUploadState.imageForUpload);
  }, [
    currentUserProfileStorage,
    imageUploadState.imageForUpload,
    imageUploadState.shouldDeleteCurrentImage,
  ]);

  const updateUserProfileDetails = useCallback(
    async (values: FormikValues) => {
      const user: UserProfileEditInput = {
        firstName: values.firstName,
        userName: values.userName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone || null,
        address: values.address,
        addressNumber: values.addressNumber,
        city: values.city,
        country: values.country,
        state: values.state,
        vat: values.vat,
        zip: values.zip
      };
      await currentUserProfileStorage.editMyUserProfileDetails(user);
      await currentUserProfileStorage.load();
      await currentUserService.load();
    },
    [currentUserProfileStorage]
  );

  const changePassword = useMemo(() => {
    return <ChangePassword />;
  }, []);

  const handleSubmit = async (values: FormikValues, formActions: FormikHelpers<FormikValues>) => {
    try {
      if (imageUploadState.shouldUpdateImage()) await updateUserImage();
      await updateUserProfileDetails(values);
      warningLeavePageService.setIsDirty(false);
      return onEditCancel();
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withForm(values, formActions);
      applicationError.withErrorMessage("default-error.edit");
      handleErrorInSnackbar(applicationError);
    }
  };

  return (
    <Grid>
      <Form
        readonly={!edit}
        formikOptions={{
          initialValues: getInitialValues(),
          onSubmit: handleSubmit,
          validateOnBlur: false,
          validateOnChange: false,
        }}
        onCancel={onEditCancel}
        rightSideActionButton={changePassword}
      >
        {({ values, setFieldValue }) => (
        <Grid container sx={{pt: 5}}>
          <Grid item xs={12} lg={4} sm={12} sx={{display: "flex", alignItems: "center", justifyContent: "center"}}>
              <ImageUpload
                readonly={!edit}
                imageUploadState={imageUploadState}
                initialImageUri={currentUserProfileStorage.currentUser?.image?.url}
                placeholderImage={HeaderProfile}
                className="my-account"
              />
          </Grid>
          <Grid container xs={12} lg={8} sm={12} sx={{mt: 10, pl: {xs: 1, sm:8, lg: 0}}} columnSpacing={10}>
            <Grid item xs={12} lg={12} sm={12} sx={{fontWeight: "bold"}}>{translationService.translate("account.information")}</Grid>
            <Grid item xs={6} lg={4} sm={4}>
                <FirstNameField value={values.firstName} readonly={!edit}/>
                <LastNameField value={values.lastName} readonly={!edit}/>
                <UserNameField
                  value={values.userName}
                  supportTag={values.isSupportAccount}
                  readonly={!edit}
                />
            </Grid>

            <Grid item xs={6} xl={4} sm={4}>
                <EmailField value={values.email} readonly={!edit}/>
                <PhoneField inOrganization={false} value={values.phone} readonly={!edit}/>
            </Grid>

            <Grid item xs={6} xl={4} sm={4}>
                <DateCreatedField value={values.registrationDate} readonly={!edit}/>
                <CreatedByField value={values.userName} readonly={!edit}/>
            </Grid>

            <Grid item xs={12}>
              {withAddress && <AddressFieldsForm values={{...values}} edit={edit} setFieldValue={setFieldValue}/>}
            </Grid>
          </Grid>
        </Grid>
        )}
      </Form>
    </Grid>
  );
});
